import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PostCard from "@/components/blog/PostCard";
import { impactStats } from "@/data/impact";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useMemberCount } from "@/hooks/useMemberCount";
import { useGivingTotals, formatCurrency } from "@/hooks/useGivingTotals";
import { usePublishedPosts } from "@/hooks/usePosts";
import { Link } from "react-router-dom";
import community1 from "@/assets/impact/community-1.jpg";
import community2 from "@/assets/impact/community-2.jpg";
import community3 from "@/assets/impact/community-3.jpg";
import community4 from "@/assets/impact/community-4.jpg";

const Impact = () => {
  const { data: counts } = useMemberCount();
  const { data: giving } = useGivingTotals();
  const { data: posts } = usePublishedPosts();
  const featuredPosts = posts?.slice(0, 3) ?? [];

  // Create dynamic stats with live member count + live giving total
  const dynamicStats = impactStats.map(stat => {
    if (stat.label === "Active Network Members") {
      return { ...stat, value: counts ? `${counts.total}+` : stat.value };
    }
    if (stat.label === "Raised for Local Nonprofits") {
      return {
        ...stat,
        value: giving && giving.total_amount > 0
          ? formatCurrency(giving.total_amount)
          : stat.value,
      };
    }
    return stat;
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 animate-fade-in">
                  Empowering Local Businesses Together
                </h1>
                <p className="text-lg text-muted-foreground animate-fade-in" style={{ animationDelay: "0.1s" }}>
                  Located in Washington Missouri and Founded in 2024, the Referral Connection Network was established to create a supportive community for local businesses. Our mission is to empower members through collaboration and shared resources, fostering a thriving ecosystem.
                </p>
                <p className="text-lg text-muted-foreground mt-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                  We value integrity and community, ensuring that every interaction is built on trust and transparency. By joining us, you can expand your professional network, gain valuable insights, and access exclusive opportunities tailored to your business needs.
                </p>
              </div>
              <div className="order-1 md:order-2 animate-fade-in" style={{ animationDelay: "0.3s" }}>
                <img
                  src="https://vkhjcsfgudgocgmvyigy.supabase.co/storage/v1/object/public/member-images/impact-group.jpg"
                  alt="RCN Members"
                  className="rounded-lg shadow-xl w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-12 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {dynamicStats.map((stat, index) => (
              <div 
                key={index} 
                className="text-center animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-4xl mb-2">{stat.icon}</div>
                <div className="text-3xl md:text-4xl font-bold mb-1">{stat.value}</div>
                <div className="text-sm md:text-base opacity-90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Initiative Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 animate-fade-in">
              RCN's Community Initiative
            </h2>
            <p className="text-lg text-muted-foreground animate-fade-in" style={{ animationDelay: "0.1s" }}>
              Our Community Initiative is at the heart of who we are. We proudly provide three free memberships to local nonprofit organizations and one free membership to a local Veteran, ensuring those who serve our community have a seat at the table.
            </p>
            <p className="text-lg text-muted-foreground mt-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>
              Each year, we organize two hands-on service projects benefiting local organizations, and every member commits to volunteering with at least two local causes annually. By growing together and giving back, we believe a rising tide truly lifts all ships, strengthening both our businesses and the community we call home.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Stories */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 animate-fade-in">
              Our Community Impact Stories
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: "0.1s" }}>
              Discover how our members are making a difference in Washington, Missouri and beyond through fundraising, community service, and collaboration.
            </p>
          </div>

          {featuredPosts.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {featuredPosts.map((post, index) => (
                  <div
                    key={post.id}
                    className="animate-fade-in"
                    style={{ animationDelay: `${0.2 + index * 0.1}s` }}
                  >
                    <PostCard post={post} />
                  </div>
                ))}
              </div>
              <div className="text-center mt-10">
                <Button asChild size="lg" variant="outline">
                  <Link to="/impact/stories">View All Stories</Link>
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-6xl mx-auto mt-12 animate-fade-in">
                {[community1, community2, community3, community4].map((img, index) => (
                  <div key={index} className="overflow-hidden rounded-lg">
                    <img
                      src={img}
                      alt={`Community impact ${index + 1}`}
                      className="w-full h-full object-cover aspect-square hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <Card className="p-12 text-center max-w-2xl mx-auto">
              <CardContent>
                <h3 className="text-xl font-semibold mb-2">More Stories Coming Soon</h3>
                <p className="text-muted-foreground">
                  Check back to see how our members continue to make an impact in our community.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 animate-fade-in">
              Our Core Values
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="text-center hover:shadow-xl transition-shadow animate-fade-in" style={{ animationDelay: "0.1s" }}>
                <CardContent className="pt-8 pb-8">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">🤝</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Integrity</h3>
                  <p className="text-muted-foreground">
                    Every interaction is built on trust and transparency, ensuring authentic connections.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-xl transition-shadow animate-fade-in" style={{ animationDelay: "0.2s" }}>
                <CardContent className="pt-8 pb-8">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">❤️</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Community</h3>
                  <p className="text-muted-foreground">
                    We foster a supportive environment where local businesses thrive together.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-xl transition-shadow animate-fade-in" style={{ animationDelay: "0.3s" }}>
                <CardContent className="pt-8 pb-8">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">🚀</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Growth</h3>
                  <p className="text-muted-foreground">
                    Shared resources and collaboration drive success for all members.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 animate-fade-in">
            Ready to Make an Impact?
          </h2>
          <p className="text-lg md:text-xl max-w-2xl mx-auto mb-8 opacity-95 animate-fade-in" style={{ animationDelay: "0.1s" }}>
            Join our community of local businesses making a difference in Washington, Missouri. Together, we can achieve more.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <Button size="lg" variant="secondary" asChild>
              <a href="/visit-a-meeting">
                Visit a Meeting
              </a>
            </Button>
            <Button size="lg" variant="outline" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary" asChild>
              <a href="/washington">View Our Chapters</a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Impact;
